import { IGame, IGameAttendances } from "@grassrootly/api";
import { PlayerGameAttendance } from "../../../screens/edit-game/useEditGame";
import { useEffect, useState } from "react";
import { MainStackParamList } from "../../../navigation/Navigation.types";
import { DrawerNavigationProp } from "@react-navigation/drawer";

export type EditGameLineupsProps = {
  attendees: IGameAttendances[];
  setAttendance: (params: PlayerGameAttendance[]) => void;
  game: IGame;
  navigation: DrawerNavigationProp<MainStackParamList, "edit-game", undefined>;
};

export interface IEditGameLineupsHook {
  attendingPlayers: IGameAttendances[];
  updateTeamAssignment: (attendee: IGameAttendances, team: string) => void;
  isInTeam: (attendee: IGameAttendances, team: string) => number;
  unsavedChanges: IGameAttendances[];
  clearChanges: () => void;
  saveChanges: () => void;
  isUpdating: (playerId: number) => boolean;
}

export function useEditGameLineups(props: EditGameLineupsProps): IEditGameLineupsHook {
  const { attendees, setAttendance } = props;
  const [attendingPlayers, setAttendingPlayers] = useState<IGameAttendances[]>([]);
  const [unsavedChanges, setUnsavedChanges] = useState<IGameAttendances[]>([]);
  const [updatingPlayers, setUpdatingPlayers] = useState<Set<number>>(new Set());

  useEffect(() => {
    clearChanges();
  }, [attendees]);

  const autoSaveTeamAssignment = async (attendee: IGameAttendances) => {
    try {
      // Ensure loader is visible for at least 500ms
      const [, ] = await Promise.all([
        new Promise(resolve => setTimeout(resolve, 500)),
        (async () => {
          const params: PlayerGameAttendance[] = [{
            playerId: attendee.player_id,
            attendance: !!attendee.attended, // Keep existing attendance status
            team: attendee.team as string,
          }];
          await setAttendance(params);
          
          // Remove from unsaved changes since it's now saved
          setUnsavedChanges(prev => prev.filter(x => x.player_id !== attendee.player_id));
        })()
      ]);
    } finally {
      // Remove loading state for this player
      setUpdatingPlayers(prev => {
        const newSet = new Set(prev);
        newSet.delete(attendee.player_id);
        return newSet;
      });
    }
  };

  const updateTeamAssignment = (attendee: IGameAttendances, team: string) => {
    // Set loading state immediately before any processing
    setUpdatingPlayers(prev => new Set([...prev, attendee.player_id]));
    
    const at = { ...attendee };
    at.team = team;
    updateLocalState(at);
    
    // Auto-save this single change (don't await to avoid blocking UI)
    autoSaveTeamAssignment(at);
  };

  const saveChanges = async () => {
    const params: PlayerGameAttendance[] = unsavedChanges.map((x) => ({
      playerId: x.player_id,
      attendance: !!x.attended, // Keep existing attendance status
      team: x.team as string,
    }));
    await setAttendance(params);
    setUnsavedChanges([]);
  };

  const clearChanges = () => {
    // Filter only attending players and sort them
    const attendingOnly = attendees
      .filter((a) => a.attended === true)
      .sort((a, b) => {
        return a.player_id > b.player_id ? 1 : a.player_id < b.player_id ? -1 : 0;
      });
    setAttendingPlayers(attendingOnly);
    setUnsavedChanges([]);
  };

  const updateLocalState = (attendee: IGameAttendances) => {
    // Update attending players list
    const ap = [...attendingPlayers];
    const exIndex = ap.findIndex((x) => x.player_id === attendee.player_id);
    if (exIndex !== -1) {
      ap.splice(exIndex, 1);
    }
    ap.push({ ...attendee });
    sortAttendances(ap);
    setAttendingPlayers(ap);
    
    // Update unsaved changes
    const uc = [...unsavedChanges];
    const existingIndex = uc.findIndex((x) => x.player_id === attendee.player_id);
    if (existingIndex !== -1) {
      uc.splice(existingIndex, 1);
    }
    uc.push({ ...attendee });
    setUnsavedChanges(uc);
  };

  const sortAttendances = (atts: IGameAttendances[]) => {
    atts.sort((a, b) => {
      return a.player_id > b.player_id ? 1 : a.player_id < b.player_id ? -1 : 0;
    });
  };

  const isInTeam = (attendee: IGameAttendances, team: string): number => {
    return attendee?.team == null ? -1 : attendee?.team == team ? 1 : 0;
  };

  const isUpdating = (playerId: number): boolean => {
    return updatingPlayers.has(playerId);
  };

  return {
    attendingPlayers,
    updateTeamAssignment,
    isInTeam,
    unsavedChanges,
    saveChanges,
    clearChanges,
    isUpdating,
  };
}
